import { useEffect, useMemo, useState } from "react";
import { Download, Share2, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Founding-member share card.
 * Renders an SVG (1080x1350 — IG Story / Pinterest portrait) with
 * member number + current FMAA city baked in, plus Download PNG + Web Share.
 */
export default function ShareCard({ memberNumber, isFounding, displayName }) {
  const [city, setCity] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api
      .get("/location/current")
      .then((r) => setCity(r.data || null))
      .catch((err) => {
        console.warn("share-card: current location failed", err?.response?.status || err?.message);
      });
  }, []);

  const num = String(memberNumber || 0).padStart(3, "0");
  const tier = isFounding ? "FOUNDING MEMBER" : "TOUR PASS";
  const cityLine = city ? `${city.label || city.city || ""}` : "Across America";
  const dateLine = new Date().toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const svgString = useMemo(
    () => buildSvg({ num, tier, cityLine, dateLine, displayName }),
    [num, tier, cityLine, dateLine, displayName]
  );

  const svgDataUrl = useMemo(
    () => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`,
    [svgString]
  );

  const shareText = `I'm Founding Member #${num} on FMAA · FuelMeh Across America. Watch the live GPS journey at www.fuelmeh.com`;

  const downloadPng = async () => {
    setDownloading(true);
    try {
      const blob = await svgToPngBlob(svgString, 1080, 1350);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fmaa-founding-${num}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast("Downloaded. Post it.");
    } catch (e) {
      toast("Couldn't render the card.");
    } finally {
      setDownloading(false);
    }
  };

  const shareNative = async () => {
    try {
      const blob = await svgToPngBlob(svgString, 1080, 1350);
      const file = new File([blob], `fmaa-founding-${num}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `FMAA Member #${num}`,
          text: shareText,
        });
        return;
      }
      // Fallback: copy text
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast("Caption copied. Paste it with the card.");
    } catch (err) {
      // User cancelled the native share sheet or the browser doesn't support it.
      console.warn("share-card: share aborted", err?.message || err);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast("Caption copied.");
    } catch (e) {
      toast("Couldn't copy.");
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-8 py-16" data-testid="founding-share-card">
      <div className="grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <div className="overline">Your share card</div>
          <h2 className="font-display text-4xl md:text-5xl uppercase mt-3 leading-[0.9] drip-shadow">
            Brag a <span className="text-[#FF1493]">little.</span>
          </h2>
          <p className="text-white/70 mt-5 max-w-md">
            Founding member numbers stop at 100 — yours is permanent. Drop this card on your IG
            story or Twitter and loop more fans onto the route.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              onClick={downloadPng}
              disabled={downloading}
              className="btn-blaze"
              data-testid="share-card-download"
            >
              <Download size={14} /> {downloading ? "Rendering..." : "Download PNG"}
            </button>
            <button
              onClick={shareNative}
              className="btn-ghost"
              data-testid="share-card-share"
            >
              <Share2 size={14} /> Share
            </button>
            <button
              onClick={copyCaption}
              className="btn-ghost"
              data-testid="share-card-copy"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy caption"}
            </button>
          </div>
          <div className="mt-5 text-[10px] font-mono-tactical text-white/40 uppercase tracking-[0.25em]">
            1080 × 1350 · Optimized for IG Story
          </div>
        </div>

        <div className="md:col-span-7">
          <img
            src={svgDataUrl}
            alt={`FMAA ${tier} card #${num}`}
            className="w-full max-w-md mx-auto border border-white/10 shadow-2xl"
            data-testid="share-card-preview"
          />
        </div>
      </div>
    </section>
  );
}

// Escape XML/SVG-significant chars so user-controlled strings (name, city) can never break the SVG document.
function escXml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

// ----- SVG builder -----
function buildSvg({ num, tier, cityLine, dateLine, displayName }) {
  const safeNum = escXml(String(num).slice(0, 4));
  const safeTier = escXml(String(tier).slice(0, 30));
  const safeDate = escXml(String(dateLine).toUpperCase().slice(0, 32));
  const safeName = escXml(
    (displayName || "Member").toString().slice(0, 24).toUpperCase()
  );
  const safeCity = escXml(cityLine.toString().slice(0, 32).toUpperCase());
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1080 1350" width="100%" preserveAspectRatio="xMidYMid meet">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#0A0A0A"/>
      <stop offset="0.55" stop-color="#1a0a0a"/>
      <stop offset="1" stop-color="#0A0A0A"/>
    </linearGradient>
    <radialGradient id="glow" cx="0.7" cy="0.2" r="0.6">
      <stop offset="0" stop-color="#FF1493" stop-opacity="0.35"/>
      <stop offset="1" stop-color="#FF1493" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="glow2" cx="0.15" cy="0.85" r="0.55">
      <stop offset="0" stop-color="#FFC107" stop-opacity="0.22"/>
      <stop offset="1" stop-color="#FFC107" stop-opacity="0"/>
    </radialGradient>
    <pattern id="grain" width="120" height="120" patternUnits="userSpaceOnUse">
      <rect width="120" height="120" fill="transparent"/>
      <circle cx="20" cy="40" r="1" fill="#ffffff" opacity="0.04"/>
      <circle cx="80" cy="90" r="1" fill="#ffffff" opacity="0.05"/>
      <circle cx="60" cy="20" r="0.8" fill="#ffffff" opacity="0.03"/>
      <circle cx="100" cy="50" r="0.9" fill="#ffffff" opacity="0.04"/>
    </pattern>
  </defs>
  <rect width="1080" height="1350" fill="url(#bg)"/>
  <rect width="1080" height="1350" fill="url(#glow)"/>
  <rect width="1080" height="1350" fill="url(#glow2)"/>
  <rect width="1080" height="1350" fill="url(#grain)"/>

  <!-- Top bar -->
  <rect x="60" y="60" width="960" height="2" fill="#FFC107"/>
  <text x="60" y="120" font-family="Impact, 'Arial Black', sans-serif" font-size="26" fill="#FFC107" letter-spacing="10">FMAA · FUELMEH ACROSS AMERICA</text>
  <text x="1020" y="120" text-anchor="end" font-family="Impact, 'Arial Black', sans-serif" font-size="22" fill="#ffffff" opacity="0.55" letter-spacing="6">${safeDate}</text>

  <!-- Tier ribbon -->
  <g transform="translate(60, 200)">
    <rect width="400" height="46" fill="#FF1493"/>
    <text x="200" y="32" text-anchor="middle" font-family="Impact, 'Arial Black', sans-serif" font-size="24" fill="#0A0A0A" letter-spacing="6">${safeTier}</text>
  </g>

  <!-- Big member number -->
  <text x="60" y="500" font-family="Impact, 'Arial Black', sans-serif" font-size="200" fill="#ffffff" letter-spacing="-6">#${safeNum}</text>
  <text x="60" y="560" font-family="Impact, 'Arial Black', sans-serif" font-size="42" fill="#FFC107" letter-spacing="4">OF 100 FOUNDING</text>

  <!-- Name -->
  <rect x="60" y="610" width="960" height="2" fill="#ffffff" opacity="0.15"/>
  <text x="60" y="690" font-family="Impact, 'Arial Black', sans-serif" font-size="74" fill="#ffffff" letter-spacing="-2">${safeName}</text>

  <!-- Pulsing dot + city -->
  <circle cx="80" cy="800" r="14" fill="#FF1E1E"/>
  <circle cx="80" cy="800" r="26" fill="none" stroke="#FF1E1E" stroke-width="3" opacity="0.4"/>
  <text x="115" y="810" font-family="Impact, 'Arial Black', sans-serif" font-size="40" fill="#ffffff" letter-spacing="3">LIVE FROM ${safeCity}</text>

  <!-- Big claim -->
  <text x="60" y="940" font-family="Impact, 'Arial Black', sans-serif" font-size="110" fill="#FF1493" letter-spacing="-3">WORTH</text>
  <text x="60" y="1050" font-family="Impact, 'Arial Black', sans-serif" font-size="110" fill="#ffffff" letter-spacing="-3">THE STOP.</text>

  <!-- Sentra line -->
  <text x="60" y="1130" font-family="Impact, 'Arial Black', sans-serif" font-size="26" fill="#ffffff" opacity="0.65" letter-spacing="6">2022 NISSAN SENTRA · 50 STATES · NO REPEATS</text>

  <!-- Footer -->
  <rect x="60" y="1200" width="960" height="2" fill="#FFC107"/>
  <text x="60" y="1260" font-family="Impact, 'Arial Black', sans-serif" font-size="32" fill="#FFC107" letter-spacing="8">WWW.FUELMEH.COM</text>
  <text x="1020" y="1260" text-anchor="end" font-family="Impact, 'Arial Black', sans-serif" font-size="26" fill="#ffffff" opacity="0.7" letter-spacing="4">@FUELMEH · @FMAA-FUELMEH</text>

  <!-- Diagonal splatter accents -->
  <g opacity="0.25">
    <circle cx="980" cy="320" r="50" fill="#FF1493"/>
    <circle cx="950" cy="380" r="18" fill="#FFC107"/>
    <circle cx="1015" cy="280" r="12" fill="#FFC107"/>
  </g>
</svg>`;
}

// Convert inline SVG string -> PNG Blob via canvas
async function svgToPngBlob(svgString, width, height) {
  const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0A0A0A";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("toBlob null"))),
        "image/png",
        0.95
      );
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}
